import { useEffect, useState } from "react";
import { supabase } from "../supabaseClient";

export function useAuth() {
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  const [businessId, setBusinessId] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    async function loadProfileForSession(activeSession) {
      if (!activeSession?.user) {
        if (!mounted) return;
        setProfile(null);
        setBusinessId(null);
        return;
      }

      try {
        // SECURITY: profile (and therefore businessId/role) is always fetched
        // using the authenticated session's user.id — never from URL params or
        // user-supplied input. This is the trust root for all downstream auth checks.
        const { data: profileData, error } = await supabase
          .from("profiles")
          .select("*, businesses(*)")
          .eq("id", activeSession.user.id)
          .maybeSingle();

        if (!mounted) return;

        if (error) {
          setProfile(null);
          setBusinessId(null);
          return;
        }

        setProfile(profileData || null);
        setBusinessId(profileData?.business_id || null);
      } catch {
        if (!mounted) return;
        setProfile(null);
        setBusinessId(null);
      }
    }

    async function bootstrapAuth() {
      try {
        const {
          data: { session: existingSession },
          error,
        } = await supabase.auth.getSession();

        if (!mounted) return;

        if (error) {
          setSession(null);
          setProfile(null);
          setBusinessId(null);
          setAuthLoading(false);
          return;
        }

        setSession(existingSession || null);
        await loadProfileForSession(existingSession);
      } catch {
        if (!mounted) return;
        setSession(null);
        setProfile(null);
        setBusinessId(null);
      } finally {
        if (mounted) setAuthLoading(false);
      }
    }

    bootstrapAuth();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      if (!mounted) return;

      setAuthLoading(true);
      setSession(nextSession || null);

      Promise.resolve(loadProfileForSession(nextSession))
        .catch(() => {})
        .finally(() => {
          if (mounted) setAuthLoading(false);
        });
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  async function handleSignOut() {
    await supabase.auth.signOut();
  }

  // If a profile row exists but has no role column yet (migration pending),
  // default to 'tradie' so existing accounts never hang on the loading screen.
  // Only return null when there is genuinely no profile row at all.
  const role = profile !== null ? (profile?.role || "tradie") : null;

  return { session, profile, businessId, role, authLoading, handleSignOut };
}
